// Data hooks for the kitchen widgets. Same pattern as src/data/queries.ts:
// one shared cache entry per key, localStorage snapshots for instant (and
// honestly-labelled) display after a reload, optimistic mutations that roll
// back and report failures.

import { useMutation, useQuery, useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { useEffect } from "react";
import type {
  KitchenList,
  KitchenSettings,
  KitchenTimer,
  ListItem,
  ListsPayload,
  Meal,
  MealFavorite,
  MealInput,
  MealSlot,
  RecentMeal,
  TimersPayload,
} from "../../../shared/kitchen/models";
import { timerPhase, timerShouldRing } from "../../../shared/kitchen/timers";
import { api } from "../../data/api";
import { readSnapshot, writeSnapshot } from "../../data/persist";

const SEC = 1000;
const MIN = 60 * SEC;

export const KEYS = {
  settings: ["kitchen", "settings"] as const,
  meals: ["kitchen", "meals"] as const,
  recent: ["kitchen", "meals-recent"] as const,
  history: ["kitchen", "meals-history"] as const,
  favorites: ["kitchen", "favorites"] as const,
  lists: ["kitchen", "lists"] as const,
  timers: ["kitchen", "timers"] as const,
};

function usePersistedQuery<T>(opts: {
  key: readonly unknown[];
  fn: (signal: AbortSignal) => Promise<T>;
  refetchMs?: number | ((data: T | undefined) => number);
  staleMs?: number;
  enabled?: boolean;
}) {
  const snapKey = JSON.stringify(opts.key);
  const staleMs = opts.staleMs ?? MIN;
  const refetch = opts.refetchMs;
  const q = useQuery({
    queryKey: opts.key,
    queryFn: ({ signal }) => opts.fn(signal),
    initialData: () => readSnapshot<T>(snapKey)?.data,
    initialDataUpdatedAt: () => readSnapshot<T>(snapKey)?.savedAt,
    staleTime: (query) => (query.state.dataUpdateCount === 0 ? 0 : staleMs),
    refetchInterval: typeof refetch === "function" ? (query) => refetch(query.state.data as T | undefined) : refetch,
    enabled: opts.enabled ?? true,
  });
  useEffect(() => {
    if (q.data !== undefined && q.dataUpdatedAt && q.status === "success" && q.fetchStatus === "idle") writeSnapshot(snapKey, q.data, q.dataUpdatedAt);
  }, [q.data, q.dataUpdatedAt, q.status, q.fetchStatus, snapKey]);
  return q;
}

/** Refetch a key once no other mutation on it is still in flight (avoids clobbering optimistic state). */
function settle(qc: QueryClient, key: QueryKey) {
  if (qc.isMutating({ mutationKey: key }) <= 1) void qc.invalidateQueries({ queryKey: key });
}

let tempSeq = 0;
// Server ids are base64url, which never contains ":", so temp ids can't collide.
const tempId = (prefix: string) => `tmp:${prefix}:${++tempSeq}`;
export const isTempId = (id: string) => id.startsWith("tmp:");

// ---------- Settings ----------

export function useKitchenSettings() {
  return usePersistedQuery({ key: KEYS.settings, fn: (s) => api<KitchenSettings>("/kitchen/settings", { signal: s }), staleMs: 5 * MIN, refetchMs: 30 * MIN });
}

export function useSaveKitchenSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<KitchenSettings>) => api<KitchenSettings>("/kitchen/settings", { method: "PUT", body: patch }),
    onSuccess: (s) => qc.setQueryData(KEYS.settings, s),
  });
}

// ---------- Meals ----------

export function useMeals(start: string, end: string) {
  return usePersistedQuery({
    key: [...KEYS.meals, start, end],
    fn: (s) => api<{ meals: Meal[] }>(`/kitchen/meals?start=${start}&end=${end}`, { signal: s }).then((r) => r.meals),
    refetchMs: 5 * MIN,
    staleMs: MIN,
  });
}

export function useMealPicks(enabled: boolean) {
  const favorites = usePersistedQuery({ key: KEYS.favorites, fn: (s) => api<MealFavorite[]>("/kitchen/favorites", { signal: s }), staleMs: 5 * MIN, enabled });
  const recent = useQuery({ queryKey: KEYS.recent, queryFn: ({ signal }) => api<RecentMeal[]>("/kitchen/meals/recent", { signal }), staleTime: 5 * MIN, enabled });
  return { favorites, recent };
}

/** Every dinner the household has planned, for "Surprise me"; fetched when it's first used. */
export function useMealHistory(enabled: boolean) {
  return useQuery({ queryKey: KEYS.history, queryFn: ({ signal }) => api<RecentMeal[]>("/kitchen/meals/history", { signal }), staleTime: 30 * MIN, enabled });
}

/** Apply a change to every cached meal range that contains `date`. */
function patchMealCaches(qc: QueryClient, date: string, fn: (meals: Meal[]) => Meal[]) {
  for (const [key, data] of qc.getQueriesData<Meal[]>({ queryKey: KEYS.meals })) {
    const [, , start, end] = key as [string, string, string, string];
    if (!data || date < start || date > end) continue;
    qc.setQueryData<Meal[]>(key, fn(data));
  }
}

export function useMealActions() {
  const qc = useQueryClient();
  const snapshot = async () => {
    await qc.cancelQueries({ queryKey: KEYS.meals });
    return { prev: qc.getQueriesData<Meal[]>({ queryKey: KEYS.meals }) };
  };
  const rollback = (_e: Error, _v: unknown, ctx: { prev: [QueryKey, Meal[] | undefined][] } | undefined) => {
    for (const [key, data] of ctx?.prev ?? []) qc.setQueryData(key, data);
  };
  const without = (date: string, slot: MealSlot) => (m: Meal) => !(m.date === date && m.slot === slot);
  const mutationKey = KEYS.meals;

  return {
    set: useMutation({
      mutationKey,
      mutationFn: ({ date, slot, input }: { date: string; slot: MealSlot; input: MealInput }) => api<Meal>(`/kitchen/meals/${date}/${slot}`, { method: "PUT", body: input }),
      onMutate: async ({ date, slot, input }) => {
        const ctx = await snapshot();
        const draft: Meal = { id: tempId("meal"), date, slot, ...input, updatedAt: new Date().toISOString() };
        patchMealCaches(qc, date, (list) => [...list.filter(without(date, slot)), draft]);
        return ctx;
      },
      onError: rollback,
      onSuccess: (meal) => {
        patchMealCaches(qc, meal.date, (list) => [...list.filter(without(meal.date, meal.slot)), meal]);
        void qc.invalidateQueries({ queryKey: KEYS.recent });
        void qc.invalidateQueries({ queryKey: KEYS.history });
      },
      onSettled: () => settle(qc, KEYS.meals),
    }),
    clear: useMutation({
      mutationKey,
      mutationFn: ({ date, slot }: { date: string; slot: MealSlot }) => api<{ ok: true }>(`/kitchen/meals/${date}/${slot}`, { method: "DELETE" }),
      onMutate: async ({ date, slot }) => {
        const ctx = await snapshot();
        patchMealCaches(qc, date, (list) => list.filter(without(date, slot)));
        return ctx;
      },
      onError: rollback,
      onSettled: () => settle(qc, KEYS.meals),
    }),
  };
}

export function useFavoriteActions() {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: KEYS.favorites });
  return {
    create: useMutation({
      mutationFn: (input: { title: string; notes?: string | null; recipeUrl?: string | null }) => api<MealFavorite>("/kitchen/favorites", { method: "POST", body: input }),
      onSuccess: (f) => qc.setQueryData<MealFavorite[]>(KEYS.favorites, (old) => [...(old ?? []), f]),
      onSettled: refresh,
    }),
    update: useMutation({
      mutationFn: ({ id, patch }: { id: string; patch: { title?: string; notes?: string | null; recipeUrl?: string | null } }) =>
        api<MealFavorite>(`/kitchen/favorites/${id}`, { method: "PATCH", body: patch }),
      onSuccess: (f) => qc.setQueryData<MealFavorite[]>(KEYS.favorites, (old) => (old ?? []).map((x) => (x.id === f.id ? f : x))),
      onSettled: refresh,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api<{ ok: true }>(`/kitchen/favorites/${id}`, { method: "DELETE" }),
      onSuccess: (_r, id) => qc.setQueryData<MealFavorite[]>(KEYS.favorites, (old) => (old ?? []).filter((x) => x.id !== id)),
      onSettled: refresh,
    }),
  };
}

// ---------- Lists ----------

export function useLists() {
  return usePersistedQuery({ key: KEYS.lists, fn: (s) => api<ListsPayload>("/kitchen/lists", { signal: s }), refetchMs: 2 * MIN, staleMs: 30 * SEC });
}

type ListsCtx = { prev?: ListsPayload };

export function useListActions() {
  const qc = useQueryClient();
  const mutationKey = KEYS.lists;
  const optimistic = async (fn: (d: ListsPayload) => ListsPayload): Promise<ListsCtx> => {
    await qc.cancelQueries({ queryKey: KEYS.lists });
    const prev = qc.getQueryData<ListsPayload>(KEYS.lists);
    if (prev) qc.setQueryData<ListsPayload>(KEYS.lists, fn(prev));
    return { prev };
  };
  const rollback = (_e: Error, _v: unknown, ctx: ListsCtx | undefined) => {
    if (ctx?.prev) qc.setQueryData(KEYS.lists, ctx.prev);
  };
  const patchItems = (fn: (items: ListItem[]) => ListItem[]) => qc.setQueryData<ListsPayload>(KEYS.lists, (d) => (d ? { ...d, items: fn(d.items) } : d));
  const onSettled = () => settle(qc, KEYS.lists);

  return {
    add: useMutation({
      mutationKey,
      mutationFn: (v: { listId: string; text: string; quantity: string | null; tempId: string }) =>
        api<ListItem>(`/kitchen/lists/${v.listId}/items`, { method: "POST", body: { text: v.text, quantity: v.quantity } }),
      onMutate: (v) =>
        optimistic((d) => {
          const key = v.text.trim().toLowerCase();
          const existing = d.items.find((i) => i.listId === v.listId && i.text.trim().toLowerCase() === key);
          if (existing) return { ...d, items: d.items.map((i) => (i === existing ? { ...i, checkedAt: null, quantity: v.quantity ?? i.quantity } : i)) };
          return { ...d, items: [...d.items, { id: v.tempId, listId: v.listId, text: v.text, quantity: v.quantity, checkedAt: null, createdAt: new Date().toISOString() }] };
        }),
      onError: rollback,
      onSuccess: (item, v) => patchItems((items) => [...items.filter((i) => i.id !== v.tempId && i.id !== item.id), item]),
      onSettled,
    }),
    toggle: useMutation({
      mutationKey,
      mutationFn: (item: ListItem) => api<ListItem>(`/kitchen/items/${item.id}`, { method: "PATCH", body: { checked: !item.checkedAt } }),
      onMutate: (item) =>
        optimistic((d) => ({ ...d, items: d.items.map((i) => (i.id === item.id ? { ...i, checkedAt: item.checkedAt ? null : new Date().toISOString() } : i)) })),
      onError: rollback,
      onSettled,
    }),
    update: useMutation({
      mutationKey,
      mutationFn: ({ item, text, quantity }: { item: ListItem; text: string; quantity: string | null }) =>
        api<ListItem>(`/kitchen/items/${item.id}`, { method: "PATCH", body: { text, quantity } }),
      onMutate: ({ item, text, quantity }) => optimistic((d) => ({ ...d, items: d.items.map((i) => (i.id === item.id ? { ...i, text, quantity } : i)) })),
      onError: rollback,
      onSettled,
    }),
    remove: useMutation({
      mutationKey,
      mutationFn: (item: ListItem) => api<{ ok: true }>(`/kitchen/items/${item.id}`, { method: "DELETE" }),
      onMutate: (item) => optimistic((d) => ({ ...d, items: d.items.filter((i) => i.id !== item.id) })),
      onError: rollback,
      onSettled,
    }),
    clearChecked: useMutation({
      mutationKey,
      mutationFn: (listId: string) => api<{ removed: number }>(`/kitchen/lists/${listId}/clear-checked`, { method: "POST", body: {} }),
      onMutate: (listId) => optimistic((d) => ({ ...d, items: d.items.filter((i) => !(i.listId === listId && i.checkedAt)) })),
      onError: rollback,
      onSettled,
    }),
  };
}

/** Admin: list structure. Not optimistic: these are rare, deliberate changes. */
export function useListAdmin() {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: KEYS.lists });
  return {
    create: useMutation({ mutationFn: (name: string) => api<KitchenList>("/kitchen/lists", { method: "POST", body: { name } }), onSettled: refresh }),
    rename: useMutation({
      mutationFn: ({ id, name }: { id: string; name: string }) => api<KitchenList>(`/kitchen/lists/${id}`, { method: "PATCH", body: { name } }),
      onSettled: refresh,
    }),
    remove: useMutation({ mutationFn: (id: string) => api<{ ok: true }>(`/kitchen/lists/${id}`, { method: "DELETE" }), onSettled: refresh }),
  };
}

// ---------- Timers ----------

export interface TimersData extends TimersPayload {
  /** server clock − local clock, measured when the data arrived. */
  skewMs: number;
}

/** Local "now" corrected to the server clock, so every screen agrees on when a timer ends. */
export const serverNowMs = (skewMs: number) => Date.now() + skewMs;

function withSkew(p: TimersPayload, startedAt: number): TimersData {
  // Assume the server stamped its clock halfway through the round trip.
  const mid = (startedAt + Date.now()) / 2;
  return { ...p, skewMs: Math.round(Date.parse(p.serverNow) - mid) };
}

export function useTimers() {
  return useQuery({
    queryKey: KEYS.timers,
    queryFn: async ({ signal }) => {
      const started = Date.now();
      return withSkew(await api<TimersPayload>("/kitchen/timers", { signal }), started);
    },
    // Modest polling: quicker while something is ringing (so a dismiss on
    // another screen silences this one soon), slower when nothing is running.
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d?.timers.length) return 30 * SEC;
      const now = serverNowMs(d.skewMs);
      return d.timers.some((t) => timerShouldRing(t, now)) ? 5 * SEC : 15 * SEC;
    },
    staleTime: 5 * SEC,
  });
}

type TimersCtx = { prev?: TimersData };

export function useTimerActions() {
  const qc = useQueryClient();
  const mutationKey = KEYS.timers;
  const optimistic = async (fn: (timers: KitchenTimer[], now: number) => KitchenTimer[]): Promise<TimersCtx> => {
    await qc.cancelQueries({ queryKey: KEYS.timers });
    const prev = qc.getQueryData<TimersData>(KEYS.timers);
    if (prev) qc.setQueryData<TimersData>(KEYS.timers, { ...prev, timers: fn(prev.timers, serverNowMs(prev.skewMs)) });
    return { prev };
  };
  const rollback = (_e: Error, _v: unknown, ctx: TimersCtx | undefined) => {
    if (ctx?.prev) qc.setQueryData(KEYS.timers, ctx.prev);
  };
  const replace = (r: { timer: KitchenTimer }, tempOrId: string) =>
    qc.setQueryData<TimersData>(KEYS.timers, (d) => (d ? { ...d, timers: [...d.timers.filter((t) => t.id !== tempOrId && t.id !== r.timer.id), r.timer] } : d));
  const onSettled = () => settle(qc, KEYS.timers);
  const map = (id: string, fn: (t: KitchenTimer, now: number) => KitchenTimer) => (timers: KitchenTimer[], now: number) => timers.map((t) => (t.id === id ? fn(t, now) : t));

  return {
    start: useMutation({
      mutationKey,
      mutationFn: (v: { label: string; durationSec: number; tempId: string }) =>
        api<{ timer: KitchenTimer; serverNow: string }>("/kitchen/timers", { method: "POST", body: { label: v.label, durationSec: v.durationSec } }),
      onMutate: (v) =>
        optimistic((timers, now) => [
          ...timers,
          { id: v.tempId, label: v.label || "Timer", durationMs: v.durationSec * 1000, endAt: new Date(now + v.durationSec * 1000).toISOString(), remainingMs: null, createdAt: new Date(now).toISOString() },
        ]),
      onError: rollback,
      onSuccess: (r, v) => replace(r, v.tempId),
      onSettled,
    }),
    pause: useMutation({
      mutationKey,
      mutationFn: (t: KitchenTimer) => api<{ timer: KitchenTimer }>(`/kitchen/timers/${t.id}/pause`, { method: "POST", body: {} }),
      onMutate: (t) => optimistic(map(t.id, (x, now) => (x.endAt ? { ...x, endAt: null, remainingMs: Math.max(1000, Date.parse(x.endAt) - now) } : x))),
      onError: rollback,
      onSuccess: (r, t) => replace(r, t.id),
      onSettled,
    }),
    resume: useMutation({
      mutationKey,
      mutationFn: (t: KitchenTimer) => api<{ timer: KitchenTimer }>(`/kitchen/timers/${t.id}/resume`, { method: "POST", body: {} }),
      onMutate: (t) => optimistic(map(t.id, (x, now) => (x.endAt ? x : { ...x, endAt: new Date(now + (x.remainingMs ?? 0)).toISOString(), remainingMs: null }))),
      onError: rollback,
      onSuccess: (r, t) => replace(r, t.id),
      onSettled,
    }),
    extend: useMutation({
      mutationKey,
      mutationFn: (t: KitchenTimer) => api<{ timer: KitchenTimer }>(`/kitchen/timers/${t.id}/extend`, { method: "POST", body: { seconds: 60 } }),
      onMutate: (t) =>
        optimistic(
          map(t.id, (x, now) => {
            if (!x.endAt) return { ...x, remainingMs: (x.remainingMs ?? 0) + MIN, durationMs: x.durationMs + MIN };
            const done = timerPhase(x, now) === "done";
            return { ...x, endAt: new Date((done ? now : Date.parse(x.endAt)) + MIN).toISOString(), durationMs: done ? MIN : x.durationMs + MIN };
          }),
        ),
      onError: rollback,
      onSuccess: (r, t) => replace(r, t.id),
      onSettled,
    }),
    remove: useMutation({
      mutationKey,
      mutationFn: (t: KitchenTimer) => api<{ ok: true }>(`/kitchen/timers/${t.id}`, { method: "DELETE" }),
      onMutate: (t) => optimistic((timers) => timers.filter((x) => x.id !== t.id)),
      onError: rollback,
      onSettled,
    }),
  };
}

export { tempId };
